import { Badge } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { formatDateSafe } from '../../../../../utilities/dateHelpers';
import { IColumn } from '../types';
import { defaultHeaderRenderer } from '../headers';
import { CellEventsHandler } from '../formatters';
import { CellProps } from '../../types';
import { TravelStatusEnum } from 'api-paths-cas';
import { TableDate, BadgeWrap } from '../styles';
import { ApplicationState } from '../../../../../store';
import { getLateArrivals } from '../../../../../utilities/getLateArrivals';
import { hasPerm, P } from '../../../../../utilities/roles/permissions';

const ArrivalStatusFormatter = (cellProps: CellProps) => {
  const {
    row: { travelArrivalDate, travelBookedDate, travelStatus, lastAcceptanceDate = '' },
  } = cellProps;
  const hasAccess = useSelector(({ auth }: ApplicationState) => hasPerm(auth.role, P.LATE_ARRIVAL_VIEW));
  const showWarning = getLateArrivals(travelBookedDate, lastAcceptanceDate, hasAccess);

  const getStatus = () => {
    switch (travelStatus) {
      case TravelStatusEnum.TravelPrevented: {
        return { text: 'Travel Prevented', colorScheme: 'red' };
      }
      case TravelStatusEnum.TravelCancelled: {
        return { text: 'Travel Cancelled', colorScheme: 'red' };
      }
      case TravelStatusEnum.Defer: {
        return { text: 'Intends to Defer', colorScheme: 'red' };
      }
      case TravelStatusEnum.Arrived: {
        return {
          text: 'Arrived',
          colorScheme: 'green',
          date: formatDateSafe(travelArrivalDate, 'DD MMM YYYY'),
        };
      }
      case TravelStatusEnum.Booked: {
        return {
          text: 'Booked',
          colorScheme: showWarning ? 'red' : 'cyan',
          date: formatDateSafe(travelBookedDate, 'DD MMM YYYY'),
        };
      }
      case TravelStatusEnum.AlreadyInUk: {
        return { text: 'Already in UK', colorScheme: 'green' };
      }
      case TravelStatusEnum.Withdraw: {
        return { text: 'Intends to Withdraw', colorScheme: 'red' };
      }
      default: {
        return { text: travelStatus, variant: 'transparent' };
      }
    }
  };

  const { colorScheme, variant, text, date } = getStatus();

  return (
    <CellEventsHandler cellProps={cellProps}>
      <BadgeWrap>
        <Badge colorScheme={colorScheme} variant={variant}>
          {text}
        </Badge>

        <TableDate>{date}</TableDate>
      </BadgeWrap>
    </CellEventsHandler>
  );
};

export const ArrStatusColumn = (): IColumn => ({
  item: {
    key: 'travelStatus',
    name: 'Arrival status',
    headerRenderer: defaultHeaderRenderer,
    width: 'minmax(160px, 1.25fr)',
    sortable: true,
    formatter: ArrivalStatusFormatter,
  },
  visible: ({ isArrStatusVisible }) => isArrStatusVisible!,
});
